import { AxisTickProps } from '@nivo/axes'
import { Bar, BarDatum, BarSvgProps } from '@nivo/bar'
import { DSTooltip } from '@repo/design-system/index'
import { QIcon_question_outline } from '@repo/icons/index'
import { useMemo, useRef, useState } from 'react'
import {
  ACTIVE_CHART_COLOR,
  GRAPH_TOOLTIP_CLS,
  INACTIVE_CHART_COLOR,
  chartWidth,
  graph_common_props,
} from '../../../../modules/consts/caf'
import useIsMobile from '../../../../modules/hooks/common/useIsMobile'
import { axisRange, maxAxisRange, useScrollPosition } from '../../../../modules/hooks/webview/caf/chart/utilFunction'
import { n_caf } from '../../../../modules/i18n/strings/auto/wording_webview'
import useTranslation from '../../../../modules/i18n/useTranslation'
import { ICON_QUESTION_GRAY } from '../../../../modules/svg/svg'
import { COLORS } from '../../../../modules/vars'
import { useWebviewModal } from '../common/Modal/WebviewModal'
import { CustomXTick, CustomYTick } from './common/CustomXYTick'

interface BarRatioChartData extends BarDatum {
  pause_ratio?: number
  filler_ratio?: number
  ngram_ratio?: number
  turn: number
  color: string
}
interface BarChartProps {
  dataKey: 'pause_ratio' | 'filler_ratio' | 'ngram_ratio'
  indexBy: 'turn'
  data: any
  tooltipName: string
}

const CHART_STYLE: Partial<BarSvgProps<BarDatum>> = {
  margin: { bottom: 40, left: 35, top: 10 },
  borderRadius: 2,
  padding: 0.3,
  valueScale: { type: 'linear' },
  indexScale: { type: 'band', round: true },
  ariaLabel: 'bar-chart',
  role: 'application',
  layout: 'vertical',
}

const BarRatioChart = ({ data, dataKey, indexBy, tooltipName }: BarChartProps) => {
  const scrollRef = useRef(null)
  const [scrollX] = useScrollPosition(scrollRef)
  const isMobile = useIsMobile()
  const { t } = useTranslation()

  const [targetBarIndex, setTargetBarIndex] = useState(undefined)

  // 서버 응답 데이터에 그래프 스타일링 추가
  const graphData: BarRatioChartData[] = useMemo(
    () =>
      data
        ?.filter((dataItem) => dataItem[dataKey] >= 0)
        ?.map((d, index) => {
          return {
            ...d,
            [dataKey]: d[dataKey] * 100,
            color: targetBarIndex !== undefined && targetBarIndex !== index ? INACTIVE_CHART_COLOR : ACTIVE_CHART_COLOR,
            [indexBy]: d[indexBy] + 1,
          }
        }),
    [data, dataKey, indexBy, targetBarIndex]
  )

  const handleClickBarItem = (e: any) => {
    const findIndex = graphData.findIndex((item) => item[indexBy] === e.data[indexBy])
    if (findIndex >= 0) {
      setTargetBarIndex(findIndex)
    }
  }

  const webviewPopup = useWebviewModal()
  const handleClickTooltip = () => {
    webviewPopup.show({
      id: 'epc_popup',
      title: t(n_caf.graph_common.user_section.modal_title),
      body: t(n_caf.graph_common.user_section.modal_subtitle),
    })
  }

  const xRange = axisRange(graphData.length, 5, 1).slice(1)
  const yMax = maxAxisRange(graphData, dataKey)
  const yAxisInterval = yMax < 20 ? 2 : yMax < 50 ? 5 : yMax < 100 ? 10 : yMax < 200 ? 20 : 50
  const yRange =
    isNaN(yMax) || isNaN(yAxisInterval)
      ? []
      : Array(Math.ceil(yMax / yAxisInterval))
          .fill(0)
          .map((_, i) => (i + 1) * yAxisInterval)

  return (
    <>
      <div ref={scrollRef} className="no-scrollbar relative h-[240px] w-full overflow-x-scroll">
        <Bar
          width={chartWidth}
          height={240}
          {...CHART_STYLE}
          data={graphData}
          indexBy={indexBy}
          keys={[dataKey]}
          // 데이터 값 표시
          enableLabel={false}
          layers={['grid', 'bars', 'axes']}
          theme={graph_common_props}
          colorBy={'indexValue'}
          colors={({ data }) => String(data.color)}
          axisBottom={{
            tickSize: 0,
            tickRotation: 0,
            tickValues: xRange,
            legendOffset: 40,
            legendPosition: 'start',
            renderTick: (tick: AxisTickProps<string>) => <CustomXTick tick={tick} yGap={20} />,
          }}
          axisLeft={{
            tickSize: 0,
            tickRotation: 0,
            tickValues: yRange,
            renderTick: (tick: AxisTickProps<string>) => (
              <CustomYTick tick={tick} scrollX={scrollX} size={10} xGap={-3} mark="%" />
            ),
          }}
          tooltip={({ value }) => <ToolTip tooltipName={tooltipName} value={value} />}
          onClick={handleClickBarItem}
          onMouseLeave={() => setTargetBarIndex(undefined)}
        />
      </div>
      <div className="text-h5 flex  items-center justify-center gap-[6px] text-center font-medium text-gray-500">
        {t(n_caf.graph_common.user_section.title)}{' '}
        {isMobile ? (
          <img
            src={ICON_QUESTION_GRAY}
            width={16}
            height={16}
            onClick={handleClickTooltip}
            alt="question"
            className="mb-4"
          />
        ) : (
          <DSTooltip
            place="bottom"
            isMultiline={true}
            isOpenOnClick={true}
            triggerComponent={
              <QIcon_question_outline fill={COLORS.GRAY600} width={16} height={16} className="mb-[1px]" />
            }
            contentComponent={t(n_caf.graph_common.user_section.modal_subtitle)}
          />
        )}
      </div>
    </>
  )
}

const ToolTip = ({ tooltipName, value }) => {
  return (
    <div className={GRAPH_TOOLTIP_CLS}>
      {tooltipName} {Number(value).toFixed(2)}%
    </div>
  )
}

export default BarRatioChart
